import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { IPageable, IPagination } from 'src/app/shared/models';
import {
  IFeedbackAccessSummary,
  FeedbackAccessMode,
  IFeedbackAccessDetailEntry,
  ISharedPacketAccess,
  ShareableEmployeeResponse,
  ShareFeedbackRequest,
} from 'src/app/shared/models/performance-management/feedback-access.model';

@Injectable({ providedIn: 'root' })
export class FeedbackAccessService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = 'api/performance-management';

  listCyclePacketAccess(
    cycleId: number,
    mode: FeedbackAccessMode,
    search: string,
    pageable: IPageable,
  ): Observable<IPagination<IFeedbackAccessSummary>> {
    let params = new HttpParams()
      .append('mode', mode)
      .append('page', pageable.page.toString())
      .append('size', pageable.size.toString());
    if (search.trim()) {
      params = params.append('search', search.trim());
    }
    return this.httpClient.get<IPagination<IFeedbackAccessSummary>>(
      `${this.baseUrl}/review-cycle/${cycleId}/packet-access`,
      { params },
    );
  }

  getCyclePacketAccessDetail(
    cycleId: number,
    employeeId: number,
    mode: FeedbackAccessMode,
  ): Observable<IFeedbackAccessDetailEntry[]> {
    const params = new HttpParams().append('mode', mode);
    return this.httpClient.get<IFeedbackAccessDetailEntry[]>(
      `${this.baseUrl}/review-cycle/${cycleId}/packet-access/${employeeId}`,
      { params },
    );
  }

  getShareableEmployees(
    cycleId: number,
    revieweeId: number,
  ): Observable<ShareableEmployeeResponse[]> {
    return this.httpClient.get<ShareableEmployeeResponse[]>(
      `${this.baseUrl}/review-cycle/${cycleId}/packet-access/shareable-employees/${revieweeId}`,
    );
  }

  sharePacket(cycleId: number, request: ShareFeedbackRequest): Observable<void> {
    return this.httpClient.post<void>(
      `${this.baseUrl}/review-cycle/${cycleId}/packet-access/share`,
      request,
    );
  }

  getSharesGrantedByCurrentUser(
    cycleId: number,
    revieweeId: number,
  ): Observable<ISharedPacketAccess[]> {
    return this.httpClient.get<ISharedPacketAccess[]>(
      `${this.baseUrl}/review-cycle/${cycleId}/packet-access/shared/${revieweeId}`,
    );
  }

  private readonly adminUrl = 'api/performance-management/packet-access';

  listAdminPacketAccess(
    mode: FeedbackAccessMode,
    search: string,
    pageable: IPageable,
  ): Observable<IPagination<IFeedbackAccessSummary>> {
    let params = new HttpParams()
      .set('mode', mode)
      .set('page', pageable.page)
      .set('size', pageable.size);
    if (search?.trim()) {
      params = params.set('search', search.trim());
    }
    if (pageable.field && pageable.order) {
      params = params.set('sort', `${pageable.field},${pageable.order}`);
    }
    return this.httpClient.get<IPagination<IFeedbackAccessSummary>>(this.adminUrl, { params });
  }

  getAdminPacketAccessDetail(
    employeeId: number,
    mode: FeedbackAccessMode,
  ): Observable<IFeedbackAccessDetailEntry[]> {
    const params = new HttpParams().set('mode', mode);
    return this.httpClient.get<IFeedbackAccessDetailEntry[]>(`${this.adminUrl}/${employeeId}`, { params });
  }

  revokeSharedAccess(accessId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.adminUrl}/shared/${accessId}`);
  }
}
